import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Minus, Plus } from 'lucide-react';
import { WaitlistCTA } from '../components/WaitlistCTA';
import { SectionIntro } from '../components/alt/SectionIntro';
import { TALK_TO_SALES_URL } from '../lib/links';
import { ToolMarquee } from '../components/alt/ToolMarquee';
import { PhotoPlate } from '../components/aiagents/PhotoPlate';
import { AgentBuilderCard, AgentCard, AGENT_TEMPLATES, ApprovalCard, CategoryChip, ConnectionsCard, SharedAgentsCard, TemplateWall } from '../components/aiagents/agentArtefacts';

/**
 * AI AGENTS — a purpose-built page, not the shared feature template.
 *
 * Every other Basics page runs through pages/FeatureDetail.tsx: name, promise,
 * a thesis, then a hairline-ruled specification. That shape is right for
 * Tracks or Groups, which are concepts. It is wrong for agents, because the
 * question a reader actually has is "what would one of these DO for me", and a
 * specification cannot answer it — only an artefact can.
 *
 * So this page is composed of product objects: a directory of agents, the
 * builder, a connections list, an approval prompt, a shared library. Each is
 * an idealised object rather than a screenshot (see agentArtefacts.tsx), and
 * three of them are paired with a photograph they break the frame of, so the
 * page reads warm rather than schematic.
 *
 * The template rendering of this same feature is preserved, unlinked, at
 * /features/ai-agents/alt.
 */

const HERO_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/4a286322-105c-48c6-85ee-68c479471576.jpg';
const CONNECT_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/1c144fe5-9ba1-4964-ae20-9841943af339.jpg';
const APPROVE_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/91f40c7c-271e-4f94-b6a5-96decd1c93af.jpg';

/** The cut-out co-worker the home page closes on, so both endings match. */
const CLOSING_FIGURE = 'https://cdn.magicpatterns.com/uploads/3CtotG8s5CuCGowVgFZ3tH/Untitled_design_(10).png';
const HERO_POINTS = ['Start from a template built for your role', 'Connected to the tools you already run on', 'Nothing acts without your approval'];

/**
 * The definition, one interaction away. A reader who already knows what an
 * agent is should not have to scroll past a paragraph explaining it, and a
 * reader who does not should not have to guess — which is exactly what
 * progressive disclosure is for.
 */
function WhatIsAnAgent() {
  const [open, setOpen] = useState(false);
  return <div className="mt-7 max-w-[58ch]" data-id="element-6627" data-mp="27hzfyokcqft8">
      <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} className="group inline-flex items-center gap-2.5 text-[15px] font-bold text-[#2B211A] transition-colors duration-200 ease-out hover:text-[#EA580C]" data-id="element-6628" data-mp="2lg7l3jrjknfh">
        <span aria-hidden="true" className="flex size-6 items-center justify-center rounded-full border border-[#E7E0D4] bg-white text-[#EA580C] transition-colors duration-200 ease-out group-hover:border-[#EA580C]" data-id="element-6629" data-mp="r26l2vk99udt">
          {open ? <Minus size={13} strokeWidth={3} data-id="element-6630" data-mp="2xs9md0af3zz7" /> : <Plus size={13} strokeWidth={3} data-id="element-6631" data-mp="32pjnhd76602v" />}
        </span>
        What is an AI agent?
      </button>
      {open && <p className="mt-4 border-l-2 border-[#EA580C] pl-4 text-[15.5px] leading-[1.7] text-[#6B5D52]" data-id="element-6632" data-mp="2nz2k3oaz503c">
          A named agent with its own role, its own working context and its own memory. You
          describe the responsibility once — the brief before every renewal call, the Monday
          recap, the invoice chase — and it keeps doing it, on a schedule or on request, inside
          the tools your team already uses.
        </p>}
    </div>;
}

/**
 * One claim beside one artefact. Alternating sides down the page, so the eye
 * crosses the column at each beat rather than running down one edge — and the
 * artefact is always the wider half, because it is the evidence.
 */
function Beat({
  title,
  body,
  visual,
  children,
  visualFirst = false
}: {
  title: React.ReactNode;
  body: string;
  visual: React.ReactNode;
  children?: React.ReactNode;
  visualFirst?: boolean;
}) {
  return <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2 md:gap-16" data-id="element-6633" data-mp="20bkdy04dgd8x">
      <div className={visualFirst ? 'md:order-2' : undefined} data-id="element-6634" data-mp="1vawlrb4epqd4">
        <SectionIntro align="left" className="max-w-[46ch]" data-id="element-6635" data-mp="1vd92xm2cbwp4">
          <h3 className="font-title text-[30px] font-bold leading-[1.1] tracking-[-0.03em] text-[#2B211A] md:text-[40px]" data-id="element-6636" data-mp="1qbjri5b7hh0i">
            {title}
          </h3>
          <p className="mt-5 text-[16.5px] leading-[1.7] text-[#6B5D52] md:text-[17.5px]" data-id="element-6637" data-mp="1gms1w3tmb8sr">{body}</p>
          {children}
        </SectionIntro>
      </div>
      <div className={visualFirst ? 'md:order-1' : undefined} data-id="element-6638" data-mp="1clgd20ikirlm">{visual}</div>
    </div>;
}
export function AIAgents() {
  return <>
      {/* ── Hero ──────────────────────────────────────────────────────────
       Copy left, evidence right. The agent card breaking off the photograph's
       left edge means you know what one of these IS before you scroll. */}
      <section className="relative overflow-hidden bg-[#FAF8F4] pb-20 pt-32 md:pb-28 md:pt-40" data-id="element-6639" data-mp="nkilgeszltuq">
        <div aria-hidden="true" className="dot-matrix pointer-events-none absolute inset-0 opacity-[0.5]" data-id="element-6640" data-mp="si0x4cye2n6f" />
        <div aria-hidden="true" className="pointer-events-none absolute -right-40 -top-24 hidden size-[560px] rounded-full bg-[#EA580C]/[0.05] blur-[120px] md:block" data-id="element-6641" data-mp="xiydcjzu975a" />

        <div className="relative mx-auto grid max-w-6xl grid-cols-1 items-center gap-14 px-6 md:grid-cols-[1fr_0.95fr] md:gap-10 md:px-10" data-id="element-6642" data-mp="4thofwbfg8dt">
          <SectionIntro align="left" data-id="element-6643" data-mp="9t40w3jflklk">
            {/* Group, then this page. */}
            <div className="flex items-center gap-3 text-[12px] font-bold uppercase tracking-[0.16em] text-[#A2917F]" data-id="element-6644" data-mp="er5i0ixar9ue">
              <span data-id="element-6645" data-mp="jixc33fnvxpa">Features</span>
              <span aria-hidden="true" className="h-px w-5 bg-[#D9CFBE]" data-id="element-6646" data-mp="jjyphek5gwa6" />
              <span className="text-[#EA580C]" data-id="element-6647" data-mp="3i7mc2e4o5g9e">AI agents</span>
            </div>

            {/* One claim, one colour. The two-tone version had to name the
             category before it could make the promise; the breadcrumb above
             already says AI agents, so the headline can just be the promise.
             The terminating stop takes the accent, as everywhere on the site. */}
            <h1 className="font-title mt-6 text-[44px] font-bold leading-[1.03] tracking-[-0.03em] text-[#2B211A] sm:text-[58px] md:text-[68px]" data-id="element-6648" data-mp="3ovt3gblm8jiv">
              Give recurring work an owner
              <span className="text-[#EA580C]" data-id="element-6649" data-mp="2w8ggi0bjm9p1">.</span>
            </h1>

            {/* Three ticks doing objection work in the first screen — fast,
             connected, safe — rather than making the reader find those
             answers 800px down the page. */}
            <ul className="mt-8 space-y-3" data-id="element-6650" data-mp="3168ixo9tde7h">
              {HERO_POINTS.map(point => <li key={point} className="flex items-start gap-3 text-[16px] text-[#4A3D33]" data-id="element-6651" data-mp="3194e54ua1os8">
                  <Check size={17} strokeWidth={3} className="mt-[3px] flex-none text-[#EA580C]" data-id="element-6652" data-mp="lhgjxh6nead4" />
                  {point}
                </li>)}
            </ul>

            <WhatIsAnAgent data-id="element-6653" data-mp="nza9tmsmkalw" />

            <div className="mt-9 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center" data-id="element-6654" data-mp="o24tvd7q7tes">
              <WaitlistCTA analyticsLabel="ai-agents-hero" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#EA580C] px-7 py-3.5 text-[15px] font-bold text-white transition-colors duration-200 ease-out hover:bg-[#C2410C]" data-id="element-6655" data-mp="sx20kdhj9qoi">
                Get started free <ArrowRight size={16} data-id="element-6656" data-mp="16run3vvuc74l" />
              </WaitlistCTA>
              <a href={TALK_TO_SALES_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-full border border-[#E7E0D4] bg-white px-7 py-3.5 text-[15px] font-bold text-[#2B211A] transition-colors duration-200 ease-out hover:border-[#D9CFBE] hover:bg-[#FFF8F3]" data-id="element-6657" data-mp="1bq5w64ebadlr">
                Talk to sales
              </a>
            </div>
            <p className="mt-6 text-[13.5px] text-[#A2917F]" data-id="element-6658" data-mp="1ug432h6di0nt">
              $100 in credits free · No card required
            </p>
          </SectionIntro>

          <PhotoPlate src={HERO_IMAGE} side="left" card={<AgentCard template={AGENT_TEMPLATES[3]} className="w-full max-w-[320px] shadow-[0_1px_2px_rgba(43,33,26,0.05),0_22px_50px_-20px_rgba(43,33,26,0.3)]" data-id="element-6660" data-mp="10yrkptgskx3x" />} data-id="element-6659" data-mp="15q9h047ve9ik" />
        </div>
      </section>

      {/* ── The argument, then five beats of evidence ────────────────────── */}
      <section className="border-t border-[#EDE6DA] bg-white py-20 md:py-28" data-id="element-6661" data-mp="qyh5627abdll">
        <div className="mx-auto max-w-6xl px-6 md:px-10" data-id="element-6662" data-mp="1onty2p4e83sm">
          <SectionIntro align="left" className="max-w-[52ch]" data-id="element-6663" data-mp="1jopqu4114g4x">
            <h2 className="font-title text-[32px] font-bold leading-[1.1] tracking-[-0.03em] text-[#2B211A] md:text-[48px]" data-id="element-6664" data-mp="1jn6bqgr40b4q">
              Built for the work that repeats
              <span className="text-[#EA580C]" data-id="element-6665" data-mp="19qjpbnj4xc0l">.</span>
            </h2>
            <p className="mt-5 text-[16.5px] leading-[1.7] text-[#6B5D52] md:text-[17.5px]" data-id="element-6666" data-mp="3b9b6gfb3qyv">
              Move past one-off prompts. Hand a recurring responsibility to an agent that knows
              your company, reads the systems you already run on, and reports back where your team
              already works.
            </p>
          </SectionIntro>

          <div className="mt-20 space-y-24 md:mt-24 md:space-y-32" data-id="element-6667" data-mp="2ew01qm8aquzw">
            <Beat visualFirst title={<>
                  Create an agent in minutes
                  <span className="text-[#EA580C]" data-id="element-6669" data-mp="2ooxqvb7sni7h">.</span>
                </>} body="Describe the role, the priorities and the outcomes it should care about — no code, no flowchart. Then choose where it runs, what it is allowed to reach, and which model sits behind it." visual={<AgentBuilderCard data-id="element-6670" data-mp="32o6vreqjupw5" />} data-id="element-6668" data-mp="2jvuu6u7pzsn2">
              <div className="mt-7 flex flex-wrap gap-2" data-id="element-6671" data-mp="32p8i4fd0myt6">
                <CategoryChip category="marketing" data-id="element-6672" data-mp="37mrvze5sx9kl" />
                <CategoryChip category="sales" data-id="element-6673" data-mp="37mi57ocxmydu" />
                <CategoryChip category="people" data-id="element-6674" data-mp="18a5w4rn6dyv0" />
              </div>
            </Beat>

            <Beat title={<>
                  Start fast with pre-built templates
                  <span className="text-[#EA580C]" data-id="element-6676" data-mp="1datvq1pgzo0u">.</span>
                </>} body="Kick off with agents designed for real jobs across sales, marketing, support, finance and HR. Then change anything about them — the brief, the schedule, the tools — to match the way your team actually works." visual={<TemplateWall data-id="element-6677" data-mp="30ms8c40vuyzi" />} data-id="element-6675" data-mp="1d8zd4u1o87zx" />

            <Beat visualFirst title={<>
                  Securely connect your work tools
                  <span className="text-[#EA580C]" data-id="element-6679" data-mp="39lne5f00m72a">.</span>
                </>} body="Generic AI doesn't know your business; a Giga agent does. Connections are made once, centrally, and every agent reaches your systems through them — never through somebody's personal login." visual={<PhotoPlate src={CONNECT_IMAGE} side="right" card={<ConnectionsCard data-id="element-6681" data-mp="3oas0979xkvfo" />} data-id="element-6680" data-mp="3jbyrtt9i3umd" />} data-id="element-6678" data-mp="34m12vb3qz0w4" />

            <Beat title={<>
                  Stay in control while agents work
                  <span className="text-[#EA580C]" data-id="element-6683" data-mp="3sd6e1cyqt503">.</span>
                </>} body="Before an agent sends a message, files a ticket or updates a record, it stops and shows you exactly what is about to happen. Approve it once, allow it always, or decline — every time, on every action." visual={<PhotoPlate src={APPROVE_IMAGE} variant="inset" card={<ApprovalCard variant="glass" data-id="element-6685" data-mp="1gqme3k3r92nd" />} data-id="element-6684" data-mp="1go8lrj0gm4ji" />} data-id="element-6682" data-mp="3o9gu50t0jble" />

            <Beat visualFirst title={<>
                  Share an agent with the whole team
                  <span className="text-[#EA580C]" data-id="element-6687" data-mp="17ovd2sk5tjzu">.</span>
                </>} body="An agent built once belongs to the group, not to the person who made it. Access follows the group it sits in, so the right people get it on day one and nobody quietly rebuilds the same thing." visual={<SharedAgentsCard data-id="element-6688" data-mp="1vlivonxgf2d4" />} data-id="element-6686" data-mp="1cny6tguow3si" />
          </div>
        </div>
      </section>

      {/* ── What an agent reaches ─────────────────────────────────────────
       The directory, at the end rather than the beginning: breadth is only
       persuasive once the reader knows what the thing does with it. */}
      {/* The home page's tool band, not a grid of ten tiles.
        A fixed 5 × 2 grid of bordered white boxes ANSWERS the claim wrongly: it
        says the directory is exactly these ten, each equally worth naming, and
        it spends ten boxes and ten labels doing it — nine other brands' logo
        colours at full saturation, louder than anything else on the page. The
        band says the opposite by moving and by fading off both edges: this is a
        strip of a much longer list. It is also already the site's own idiom for
        exactly this claim, on ink, with the marks flattened to white
        silhouettes so the row reads as our directory rather than a collage. */}
      <section className="relative overflow-hidden bg-[#1A120C] py-20 md:py-28" data-id="element-6689" data-mp="2agpeyozsuc8z">
        <div aria-hidden="true" className="dot-matrix-dark pointer-events-none absolute inset-0 opacity-40" data-id="element-6690" data-mp="2aish27e87vu2" />
        <div className="relative mx-auto max-w-6xl px-6 md:px-10" data-id="element-6691" data-mp="8p2uz7ebrhm0">
          <SectionIntro align="left" className="max-w-[52ch]" data-id="element-6692" data-mp="8pufnn6dvuxi">
            <h2 className="font-title text-[32px] font-bold leading-[1.1] tracking-[-0.03em] text-[#FAF8F4] md:text-[44px]" data-id="element-6693" data-mp="cpmtofejqta9">
              Bring your work knowledge with you
              <span className="text-[#EA580C]" data-id="element-6694" data-mp="1t7dgh6naljqv">.</span>
            </h2>
            <p className="mt-5 text-[16.5px] leading-[1.7] text-[#C9BFB4] md:text-[17.5px]" data-id="element-6695" data-mp="1t50r48f7g73s">
              Connect Slack, Salesforce, Notion, Drive, your databases and hundreds more. Your
              agents work from real context instead of a generic prompt.
            </p>
          </SectionIntro>
        </div>

        {/* Full width, outside the container: the band's edge fade only reads
         as "continues past the frame" if the frame is the viewport. */}
        <div className="relative mt-14" data-id="element-6696" data-mp="2pw2zx1ekikey">
          <ToolMarquee data-id="element-6697" data-mp="2pv054ie4jxtr" />
        </div>

        <div className="relative mx-auto mt-14 max-w-6xl px-6 md:px-10" data-id="element-6698" data-mp="2ky1bs22abuwm">
          {/* The dark-panel action pattern used everywhere else on the site —
           one white pill — rather than an orange text link, which was the only
           place a bare coloured link stood in for a primary action. */}
          <Link to="/integrations" className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-[15px] font-bold text-[#2B211A] transition-[background-color,transform] duration-200 ease-out hover:-translate-y-px hover:bg-[#F5EFE7]" data-id="element-6699" data-mp="sxvmlews2ok2">
            Explore integrations
            <ArrowRight size={16} className="transition-transform duration-200 ease-out group-hover:translate-x-0.5" data-id="element-6700" data-mp="16xn5nu25iv4x" />
          </Link>
        </div>
      </section>

      {/* ── Close ─────────────────────────────────────────────────────────
       The home page's closing composition: an INSET ink card on cream with the
       cut-out co-worker anchored to its bottom-right and breaking the top
       edge. The full-bleed video plate was a different object on every count —
       edge to edge rather than a card, a cropped frame rather than a cut-out —
       and a crop of a figure can never do what a cut-out breaking the frame
       does, because the panel's own edge always ends up cutting the body. */}
      <section className="relative overflow-hidden bg-[#FAF8F4] pb-20 md:pb-28" data-id="element-6701" data-mp="3k43aqcqfsg81">
        <div aria-hidden="true" className="dot-matrix pointer-events-none absolute inset-0 opacity-[0.5]" data-id="element-6702" data-mp="3f61s1swxaix3" />
        <div className="relative mx-auto max-w-6xl px-6 md:px-10" data-id="element-6703" data-mp="3a671jqh27vck">
          {/* The top margin is CLEARANCE FOR THE FIGURE, not section spacing. */}
          <div className="relative mt-24 rounded-[24px] bg-[#1A120C] px-6 py-12 shadow-[0_40px_100px_-45px_rgba(43,33,26,0.7)] sm:rounded-[28px] sm:px-10 sm:py-14 md:mt-32 md:px-14 md:py-20 lg:mt-40" data-id="element-6704" data-mp="35ahul22pnc68">
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden rounded-[24px] sm:rounded-[28px]" data-id="element-6705" data-mp="2rgqm7x0ptyc3">
              <div className="dot-matrix-dark absolute inset-0 opacity-40" data-id="element-6706" data-mp="2mifebrtzdxwi" />
            </div>

            {/* Sized by HEIGHT and stood on the panel's bottom edge, so all of
             the overflow lands at the TOP where the break-out reads. */}
            <img src={CLOSING_FIGURE} alt="" aria-hidden="true" className="pointer-events-none absolute bottom-[-18px] right-[-6px] z-10 hidden h-[470px] w-auto max-w-none select-none lg:block xl:h-[520px]" data-id="element-6707" data-mp="2dj2bhi0d47kr" />

            <div className="relative lg:pr-[380px] xl:pr-[440px]" data-id="element-6708" data-mp="27sks2hekouxk">
              <SectionIntro align="left" data-id="element-6709" data-mp="1twqrclnydbvj">
                <h2 className="font-title text-[32px] font-bold leading-[1.06] tracking-[-0.03em] text-[#FAF8F4] md:text-[48px]" data-id="element-6710" data-mp="1ytzaopku7lyd">
                  Put an agent on it this week
                  <span className="text-[#EA580C]" data-id="element-6711" data-mp="2mm7f360mo4lf">.</span>
                </h2>
                <p className="mt-5 max-w-[46ch] text-[16.5px] leading-[1.7] text-[#C9BFB4]" data-id="element-6712" data-mp="2qkn9vw2yah9e">
                  Pick one job that happens every week, hand it over, and see what comes back.
                </p>
                <div className="mt-9 flex flex-col items-stretch gap-x-8 gap-y-4 sm:flex-row sm:items-center" data-id="element-6713" data-mp="2hra61z3clv8j">
                  <WaitlistCTA analyticsLabel="ai-agents-close" className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-[15px] font-bold text-[#2B211A] shadow-[0_14px_30px_-14px_rgba(0,0,0,0.6)] transition-[background-color,transform] duration-200 ease-out hover:-translate-y-px hover:bg-[#F5EFE7] sm:py-3.5" data-id="element-6714" data-mp="35gutovag5go5">
                    Get started free <ArrowRight size={16} data-id="element-6715" data-mp="3tcpui11e991n" />
                  </WaitlistCTA>
                  <a href={TALK_TO_SALES_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-[15px] font-bold text-white transition-colors duration-200 ease-out hover:text-white/70" data-id="element-6716" data-mp="3oeo9vbobl1o2">
                    Talk to sales
                  </a>
                </div>
                <p className="mt-7 text-[13px] text-white/45" data-id="element-6717" data-mp="3aokpyrcwxk1s">
                  $100 in credits included · no card needed
                </p>
              </SectionIntro>

              {/* The figure appears on phones too, seated on the panel's bottom
               edge rather than breaking out — hidden below lg the panel had no
               character at all. */}
              <img src={CLOSING_FIGURE} alt="" aria-hidden="true" className="pointer-events-none mx-auto -mb-12 mt-10 block h-[210px] w-auto max-w-none select-none sm:-mb-14 sm:h-[250px] lg:hidden" data-id="element-6718" data-mp="2fepju2zurni1" />

              <div className="relative mt-12 border-t border-white/10 pt-6 lg:mt-14" data-id="element-6719" data-mp="2oem4zun1a48c">
                <Link to="/features/pulses" className="group inline-flex items-center gap-2 text-[14px] font-bold text-white/55 transition-colors duration-200 ease-out hover:text-white" data-id="element-6720" data-mp="2t5uhx0nko7q9">
                  Next: Pulses
                  <ArrowRight size={15} className="transition-transform duration-200 ease-out group-hover:translate-x-0.5" data-id="element-6721" data-mp="333ijoajlifhx" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>;
}